import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, createFilterOptions } from '@mui/material'
import { useFormContext, Controller } from 'react-hook-form'

import { TextField, SelectMenuItem } from '../styles/TextField'
import { InputWrapperDiv } from '../styles/Containers'
import Label from './Label'

const getError = (id, errors) => {
  return id.split('.').reduce((o, i) => {
    const exists = o[i] !== undefined
    if (!exists) return {}
    return o[i]
  }, errors)
}

const filter = createFilterOptions()
const OPTION_HEIGHT = 48
const LISTBOX_VERTICAL_PADDING = 16

function AutocompleteMultiSelect({
  required = false,
  id,
  label,
  options,
  defaultValue = [],
  disabled = false,
  tooltip = '',
  allowCustomValues = true,
  maxVisibleOptions
}) {
  const [updatedOptions, setUpdatedOptions] = useState(options)

  const { control, formState } = useFormContext()

  const errors = formState.errors
  const error = getError(id, errors)

  return (
    <InputWrapperDiv>
      <Controller
        control={control}
        name={id}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ref } }) => (
          <Autocomplete
            multiple
            id={id}
            options={updatedOptions}
            getOptionLabel={option => option.label}
            getOptionKey={option => option.id}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            defaultValue={defaultValue}
            value={value}
            disabled={disabled}
            disableCloseOnSelect
            slotProps={
              maxVisibleOptions ? { listbox: { sx: { maxHeight: `${maxVisibleOptions * OPTION_HEIGHT + LISTBOX_VERTICAL_PADDING}px` } } } : undefined
            }
            renderOption={(props, option) => {
              const { key, ...optionProps } = props
              return (
                <SelectMenuItem key={key || option.id || option.inputValue} {...optionProps}>
                  {option.label}
                </SelectMenuItem>
              )
            }}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              const { inputValue } = params
              const isExisting = options.some(option => inputValue.toLowerCase() === option.label.toLowerCase())
              if (allowCustomValues && inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  label: `Add "${inputValue}"`
                })
              }
              return filtered
            }}
            onChange={(event, newValue) => {
              if (allowCustomValues && newValue && newValue[newValue.length - 1]?.inputValue) {
                const newOption = {
                  id: `temp-${newValue[newValue.length - 1].inputValue.replace(/\s+/g, '-')}`.toLowerCase(),
                  label: newValue[newValue.length - 1].inputValue
                }
                setUpdatedOptions(prev => [...prev, newOption])
                onChange([...newValue.slice(0, -1), newOption])
              } else {
                onChange(newValue)
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                inputRef={ref}
                variant="standard"
                label={<Label label={label} tooltip={tooltip} />}
                InputLabelProps={{
                  shrink: true,
                  required,
                  focused: false
                }}
                error={Object.keys(error).length > 0 && !!error}
                helperText={error?.message}
                FormHelperTextProps={{ role: 'alert' }}
              />
            )}
          />
        )}
      />
    </InputWrapperDiv>
  )
}

AutocompleteMultiSelect.propTypes = {
  id: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  allowCustomValues: PropTypes.bool,
  maxVisibleOptions: PropTypes.number
}

export default AutocompleteMultiSelect
